import { setNestedObjectValues } from "formik";
import React, { FunctionComponent, useEffect } from "react";
import { usePageContext } from "../providers/PageProvider";

export interface PageControllerProps {
  /**
   * The children should consist of Page components.
   * To enable Transact Application Framework support you must ensure the Pages have `id` props.
   */
  children?: React.ReactNode | React.ReactNodeArray | any;
}

/**
 * Allows multiple pages to be added to an application.
 * <br>
 * This component initializes the pages and loads them into the page context state.
 * <br>
 * If you are not using the Transact Application Framework, ensure end of flow pages i.e. SaveSuccess
 * are at the end of the PageController children.
 *
 * ## Example
 * ```html
 * <PageController>
 *    <Page id="LandingPage" offMenu>
 *      <LandingPage />
 *    </Page>
 *    <Page id="GettingStarted" label="Let's get started">
 *      <GettingStarted />
 *    </Page>
 *    <Page id="AboutYou" label="All about you">
 *      <AboutYou />
 *    </Page>
 *    <Page id="SubmitSuccess" offMenu>
 *      <SubmitSuccess />
 *    </Page>
 *    <Page id="SaveSuccess" offMenu>
 *      <SaveSuccess />
 *    </Page>
 *    <Page id="CancelSuccess" offMenu>
 *      <CancelSuccess />
 *    </Page>
 * </PageController>
 * ```
 */
export const PageController: FunctionComponent<PageControllerProps> = ({
  children
}: PageControllerProps) => {
  const { getCurrentPage, initPages, getFormApi } = usePageContext();

  useEffect(() => {
    const pages = React.Children.toArray(children);

    async function pageValidation (): Promise<boolean> {
      const formApi = getFormApi();
      let valid = true;
      formApi.setSubmitting(true);
      // set touched so everything gets validated
      // validate
      try {
        valid = !Object.keys(await formApi.validateForm()).length;
        // if valid reset touched for next page
        if (valid) {
          formApi.setTouched({});
        } else {
          formApi.setTouched(setNestedObjectValues(formApi.values, true));
        }
        formApi.setSubmitting(false);
        return valid;
      } catch (e) {
        formApi.setSubmitting(false);
        return valid;
      }
    }

    initPages(pages, pageValidation);
  }, []);

  return getCurrentPage();
};
